import React, { useEffect, useMemo, useState } from "react";
import styles from "../GeneralInformation/GeneralInformation.module.scss";
import {
  CircularProgressbarWithChildren,
  buildStyles,
} from "react-circular-progressbar";
import { useAppSelector } from "../../../redux/hooks";
import Icon from "../../common/Icon/Icon";
import Dropdown from "../../common/Dropdown/Dropdown";
import { useTranslation } from "react-i18next";
import Editor from "../../common/Editor/Editor";
import { INDIVIDUAL_RECOGNITION } from "../../../constants/dataConstants";
import {
  useGetRecognitionDataQuery,
  useCreateRecognitionMutation,
} from "../../../redux/services/leaderProfileRecognitionAPI";
import { useProfileParams } from "../../../utils/common";
import isEmpty from "lodash/isEmpty";
import { isValidResponse } from "../../../utils/validateAPIResponse";
import ABOBackgroundLoader from "../../common/Loaders/ABOBackgroundLoader/ABOBackgroundLoader";

const Recognition = () => {
  const { t } = useTranslation();
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const [isEditing, setIsEditing] = useState(false);
  const [recognitionData, setRecognitionData] = useState({
    completionRate: 0,
    preferredRecognitionType: 0,
    recognitionDetail: "",
  });
  const { affiliateCode, aboNumber } = useProfileParams();

  const {
    data: recognitionResponseData,
    isFetching: isLoadingGetRecognition,
    isError: isFetchRecognitionError,
  } = useGetRecognitionDataQuery(
    {
      affiliateCode,
      aboNumber,
    },
    { refetchOnMountOrArgChange: true }
  );

  const [
    createRecognition,
    {
      isLoading: isLoadingCreateRecognition,
      isError: isCreateRecognitionError,
    },
  ] = useCreateRecognitionMutation();

  useEffect(() => {
    if (!isEmpty(recognitionResponseData)) {
      if (isValidResponse(recognitionResponseData?.statusCode)) {
        setRecognitionData(
          !isEmpty(recognitionResponseData?.body)
            ? recognitionResponseData?.body
            : {}
        );
      }
    }
  }, [recognitionResponseData]);

  const handleSelectIndividualRecog = (selectedForm: any) => {
    setRecognitionData((prev) => ({
      ...prev,
      preferredRecognitionType: selectedForm?.code,
    }));
  };
  const handleSave = () => {
    createRecognition({
      affiliateCode,
      aboNumber,
      payload: recognitionData,
    }).then((resp: any) => {
      const recognitionRestData = resp?.data?.body;
      if (!isEmpty(recognitionRestData)) {
        setRecognitionData(recognitionRestData);
      }
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    const existingData = recognitionResponseData?.body;
    setRecognitionData({
      completionRate: existingData?.completionRate,
      preferredRecognitionType: existingData?.preferredRecognitionType || 0,
      recognitionDetail: existingData?.recognitionDetail || "",
    });
    setIsEditing(false);
  };

  const handleChangeDesc = (value: string) => {
    setRecognitionData((prev) => ({ ...prev, recognitionDetail: value }));
  };

  const preferredRecognitionValue = useMemo(() => {
    return INDIVIDUAL_RECOGNITION?.find(
      (val) => val?.code === recognitionData?.preferredRecognitionType
    );
  }, [recognitionData]);
  if (isLoadingGetRecognition || isLoadingCreateRecognition) {
    return <ABOBackgroundLoader num={1} heading={t("recognition")} />;
  }
  return (
    <div className={styles.general_outer_wrapper}>
      <div className={styles.title_wrapper}>
        <div className={styles.title_inner}>
          <div className={styles.progress_bar}>
            <CircularProgressbarWithChildren
              value={recognitionData?.completionRate}
              strokeWidth={20}
              className={styles["circular_progress_gradient"]}
              styles={buildStyles({
                trailColor: currentTheme === "light" ? "#D9D9D9" : "#282828",
              })}
            />
          </div>
          <div className={styles.general_info_title}>{t("recognition")}</div>
        </div>
        <div className={styles.edit_icon_wrapper}>
          {isEditing ? (
            <>
              <span
                className={styles.cancel_text}
                onClick={handleCancel}
                data-testid="recognition_cancel_testID"
              >
                {t("btnCancel")}
              </span>
              <span
                className={styles.save_text}
                onClick={handleSave}
                data-testid="recognition_save_testID"
              >
                {t("btnSave")}
              </span>
            </>
          ) : (
            <div
              className={styles.edit_wrapper}
              onClick={() => setIsEditing(true)}
              data-testid="edit_recognition_testID"
            >
              <Icon
                name="edit_goal_icon"
                size={14}
                color={currentTheme === "light" ? "#2C2C2C" : "#D8D8D9"}
                className={styles.search_icon}
              />
              <span className={styles.edit_text}>{t("edit")}</span>
            </div>
          )}
        </div>
      </div>

      <div className={`${styles.general_inner_wrapper} ${styles.two_col}`}>
        <div
          className={styles.first_column_wrapper}
          data-testid="recognition_form_testID"
        >
          <div className={styles.general_info}>
            <span className={styles.title}>
              {t("individual_recognition_title")}:
            </span>
            {isEditing ? (
              <Dropdown
                dropdownName={t("select_form")}
                items={INDIVIDUAL_RECOGNITION}
                selectedItem={preferredRecognitionValue}
                handleSelectItemCallback={handleSelectIndividualRecog}
                size="md"
              />
            ) : (
              <span className={styles.description}>
                {preferredRecognitionValue?.value || "-"}
              </span>
            )}
          </div>
        </div>
        <div
          className={styles.second_column_wrapper}
          data-testid="recognition_description_testID"
        >
          <div className={styles.general_info}>
            <span className={styles.title}>{t("description")}:</span>
            <Editor
              id="addRecognitionDetail"
              isEditing={isEditing}
              value={recognitionData?.recognitionDetail || ""}
              onChange={handleChangeDesc}
              formatterBarPlaceholder={t("add_description")}
              section="recognition_detail"
              normalize={true}
              formatterBarWrapperClassName={styles.text_area_section}
              htmlToPdfContainerClassName={styles.description}
              maxCharacter={300}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Recognition;
